import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchArticle } from '../../actions/news';
import { addToArrayReplaceOldestIfExceeds } from '../common';
import { newsProvider } from '../newsProvider';
import { postMetric } from '../userMetrics';
import useActions from './useActions';
import useLocalize from './useLocalize';

const READ_NEWS_LIMIT = 10000;


/**
 * Custom hook for managing news article modal state and read tracking
 *
 * @returns {Object} Hook return object
 * @returns {boolean} open - Whether the modal is open
 * @returns {Object|null} selectedArticle - Currently selected article
 * @returns {Array} articlesRead - Array of read article IDs
 * @returns {Function} openArticle - Function to open an article in the modal
 * @returns {Function} closeModal - Function to close the modal
 */
const useNewsArticleModal = () => {
  const localize = useLocalize();

  // Actions
  const [getArticle] = useActions([fetchArticle]);

  // States
  const [open, setOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articlesRead, setArticlesRead] = useState([]);
  const [initialized, setInitialized] = useState(false);

  // Redux Store
  const user = useSelector((state) => {
    return ((state.user) || {});
  });

  // Initialize then persist: avoid writing before initial load completes
  useEffect(() => {
    // Initialization branch
    if (!initialized) {
      try {
        const storedArticles = localStorage.getItem('news.read');
        if (storedArticles) {
          const articles = JSON.parse(storedArticles);
          if (Array.isArray(articles)) {
            // Normalize all stored IDs to strings to ensure consistent comparison
            setArticlesRead(articles.map((a) => a != null ? String(a) : a));
          }
        }
      } catch (error) {
        // Handle invalid JSON gracefully by initializing with empty array
        setArticlesRead([]);
      } finally {
        setInitialized(true);
      }
      return; // Skip persistence on same tick
    }

    // Persistence branch (post-initialization)
    if (articlesRead && articlesRead.length > 0) {
      try {
        localStorage.setItem('news.read', JSON.stringify(articlesRead));
      } catch (error) {
        // Handle quota exceeded or other localStorage errors
        console.warn('Failed to save read articles to localStorage:', error);
      }
    }
  }, [initialized, articlesRead]);

  // Add article to localStorage as read
  const addArticlesToLocalStorage = useCallback((articleId) => {
    // Normalize to string to avoid mismatches between numeric and string IDs
    const normalizedId = articleId != null ? String(articleId) : null;
    if (normalizedId != null && articlesRead && !articlesRead.includes(normalizedId)) {
      const updatedArticles = addToArrayReplaceOldestIfExceeds(
        normalizedId,
        articlesRead,
        READ_NEWS_LIMIT
      );
      setArticlesRead(updatedArticles);
    }
  }, [articlesRead]);

  // Open article in modal
  const openArticle = useCallback((article) => {
    setSelectedArticle(article);
    setOpen(true);
    getArticle(article.articleId);
    addArticlesToLocalStorage(article.articleId);

    // Post metrics
    postMetric({
      metricType: 'News',
      metricName: 'FetchNewsArticle',
      customData: {
        articleId: article.articleId,
        userId: user.encryptedId,
        provider: localize(newsProvider(article.provider)),
      }
    });
  }, [getArticle, addArticlesToLocalStorage, user, localize]);

  // Close modal
  const closeModal = useCallback(() => {
    setOpen(false);
    setSelectedArticle(null);
  }, []);

  return {
    open,
    selectedArticle,
    articlesRead,
    openArticle,
    closeModal
  };
};

export default useNewsArticleModal;